import {useMutation, useQuery} from "@tanstack/react-query";
import type {
    Heat,
    HeatBracket,
    HeatForm,
    HeatRegistration,
    MatchDetail,
    Player,
    PlayerRegistration
} from '@iziping/common';
import { apiClient } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import type {ApiError} from '@iziping/common';
import type {FinishMatchParams} from "../matches/matchesHooks";

export const useGetHeat = (id?: number) => {
    return useQuery<Heat>({
        queryKey: [QUERY_CACHE.HEAT],
        queryFn: async () => {
            if (!id) throw new Error("ID manquant");
            const response = await apiClient.get<Heat>(ENDPOINTS.heat(id));
            return response.data;
        },
        enabled: !!id
    })
};

export const useSaveHeat = () => {
    return useMutation<Heat, ApiError, { id?: number; data: HeatForm }>({
        mutationFn: async ({ id, data }) => {
            if (id) {
                // update
                const response = await apiClient.put<Heat>(`${ENDPOINTS.heat(id)}`, data);
                return response.data;
            } else {
                // create
                const response = await apiClient.post<Heat>(ENDPOINTS.heats, data);
                return response.data;
            }
        },
    });
};

interface HeatRegistrationParams {
    heatId: number;
    players: Player[];
}

export const useCreateHeatRegistration = () => {
    return useMutation<PlayerRegistration, ApiError, any>({
        mutationKey: [QUERY_CACHE.CREATE_HEAT_REGISTRATION],
        mutationFn: async (params: HeatRegistrationParams) => {
            const response = await apiClient.post<PlayerRegistration>(ENDPOINTS.registerToHeat(params.heatId), params.players);
            return response.data;
        },
    });
}

interface RemoveHeatRegistrationParams {
    id: number;
    registrationId: number;
}

export const useRemoveHeatRegistration = () => {
    return useMutation<void, ApiError, any>({
        mutationKey: [QUERY_CACHE.REMOVE_HEAT_REGISTRATION],
        mutationFn: async (params: RemoveHeatRegistrationParams) => {
            const response = await apiClient.delete(ENDPOINTS.removeFromHeat(params.id, params.registrationId));
            return response.data;
        },
    });
}

export const useGetHeatRegistrations = (id: number) => {
    return useQuery<HeatRegistration[], ApiError>({
        queryKey: [QUERY_CACHE.HEAT_REGISTRATIONS],
        queryFn: async () => {
            const response = await apiClient.get<HeatRegistration[]>(`${ENDPOINTS.heatRegistrations(id)}`);
            return response.data;
        },
        enabled: true,
    });
};

export const useGetNotRegistered = (id: string) => {
    return useQuery<PlayerRegistration[], ApiError>({
        queryKey: [QUERY_CACHE.HEAT_NOT_REGISTERED],
        queryFn: async () => {
            const response = await apiClient.get<PlayerRegistration[]>(`${ENDPOINTS.findPlayersToRegister(id)}`);
            return response.data;
        },
        enabled: true,
    });
};

interface ComputeSeedsParams {
    heatId: string;
}

export const useCreateComputeSeeds = () => {
    return useMutation<unknown, ApiError, any>({
        mutationKey: [QUERY_CACHE.COMPUTE_SEEDS],
        mutationFn: async (params: ComputeSeedsParams) => {
            const response = await apiClient.post<HeatRegistration[]>(ENDPOINTS.computeSeeds(params.heatId));
            return response.data;
        },
    });
}


export interface UpdateSeedsParams {
    heatId: number;
    players?: HeatRegistration[];
}

export const useUpdateSeeds = () => {
    return useMutation<HeatRegistration[], ApiError, any>({
        mutationKey: [QUERY_CACHE.MATCH_MODIFY],
        mutationFn: async (params: UpdateSeedsParams) => {
            const response = await apiClient.put<HeatRegistration[]>(ENDPOINTS.updateSeeds(params.heatId), params.players);
            return response.data;
        },
    });
}

interface LaunchHeatParams {
    heatId: string;
}

export const useLaunchHeat = () => {
    return useMutation<unknown, ApiError, any>({
        mutationKey: [QUERY_CACHE.LAUNCH_HEAT],
        mutationFn: async (params: LaunchHeatParams) => {
            const response = await apiClient.post(ENDPOINTS.launchHeat(params.heatId));
            return response.data;
        },
    });
}

export const useCancelHeat = () => {
    return useMutation<unknown, ApiError, any>({
        mutationKey: [QUERY_CACHE.CANCEL_HEAT],
        mutationFn: async (params: LaunchHeatParams) => {
            const response = await apiClient.post(ENDPOINTS.cancelHeat(params.heatId));
            return response.data;
        },
    });
}

export const useGetHeatsForTournament = (id: number) => {
    return useQuery<Heat[], ApiError>({
        queryKey: [QUERY_CACHE.GET_HEATS_FOR_TOURNAMENT],
        queryFn: async () => {
            const response = await apiClient.get<Heat[]>(`${ENDPOINTS.heatsTournament(id)}`);
            return response.data;
        },
        enabled: true,
    });
};

/*
export const useGetHeatBracket = (id: number) => {
    return useQuery<HeatBracket, ApiError>({
        queryKey: [QUERY_CACHE.GET_HEAT_BRACKET],
        queryFn: async () => {
            const response = await apiClient.get<HeatBracket>(`${ENDPOINTS.getFinalBracketForHeat(id)}`);
            return response.data;
        },
        enabled: true,
    });
};*/

export const useGetHeatBracket = (heatId?: number) => {
    return useQuery<HeatBracket, ApiError> ({
        queryKey: [QUERY_CACHE.GET_HEAT_BRACKET],
        queryFn: async () => {
            const response = await apiClient.get<HeatBracket>(`${ENDPOINTS.getFinalBracketForHeat(heatId!)}`);
            return response.data;
        },
        enabled: !!heatId, // évite l’appel si heatId est undefined
    });
};